import { Alert, Box, Button, Chip, Divider, Grid, List, ListItem, ListItemText, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllProfileGroups, getProfileGroupById } from 'src/shared/services/profileGroupService';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import FolderIcon from '@mui/icons-material/Folder';
import { CreateMemberProfileGroup } from 'src/modules/memberProfile/components/createMemberProfileGroup';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

type OnboardingStepProfilesProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepProfiles = ({ onNavigateAway }: OnboardingStepProfilesProps) => {
  const { t } = useTranslation(['onboarding', 'memberProfile']);
  const isMounted = useIsMounted();
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllProfileGroups(false, true);
      const groups = res.data.results || [];
      // Hämta items per grupp
      const groupsWithItems = await Promise.all(
        groups.map(async (group) => {
          try {
            const detail = await getProfileGroupById(group.memberProfile_id);
            return { ...group, profileItems: detail.data.profileItems || [] };
          } catch {
            return group;
          }
        }),
      );
      if (isMounted()) setProfileGroups(groupsWithItems);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.profiles.description')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.profiles.hint')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.profiles.currentProfiles')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.profiles.editHintPrefix')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.profiles.editHintLink')}
              </Button>
              .
            </Alert>
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant='rounded' height={48} />
                <Skeleton variant='rounded' height={48} />
              </Box>
            )}
            {!loading && profileGroups.length === 0 && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {t('onboarding:stepContent.profiles.noProfiles')}
              </Alert>
            )}
            {!loading && profileGroups.length > 0 && (
              <List disablePadding sx={{ mb: 2 }}>
                {profileGroups.map((group) => (
                  <Box key={group.memberProfile_id}>
                    <ListItem sx={{ px: 1 }}>
                      <FolderIcon sx={{ color: 'text.secondary', mr: 1.5 }} fontSize='small' />
                      <ListItemText
                        primary={group.title}
                        secondary={group.profileItems && group.profileItems.length > 0 ? undefined : t('onboarding:stepContent.profiles.noItems')}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      <Typography variant='caption' color='text.secondary'>
                        {group.profileItems?.length || 0} {t('onboarding:stepContent.profiles.items')}
                      </Typography>
                    </ListItem>
                    {group.profileItems && group.profileItems.length > 0 && (
                      <Box sx={{ pl: 5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {group.profileItems.map((item) => (
                          <Chip key={item.memberProfileItem_id} label={item.name} size='small' variant='outlined' />
                        ))}
                      </Box>
                    )}
                    <Divider />
                  </Box>
                ))}
              </List>
            )}
          </Box>

          <Button variant='outlined' startIcon={<AddCircleOutlineIcon />} onClick={() => setCreateModalOpen(true)} sx={{ mb: 2, mt: 2 }}>
            {t('memberProfile:profileGroupPage.createProfileGroup')}
          </Button>
        </Grid>
      </Grid>

      {createModalOpen && (
        <CreateMemberProfileGroup
          modalOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={() => {
            setCreateModalOpen(false);
            fetchProfiles();
          }}
        />
      )}
    </Box>
  );
};
